import React from 'react';
import { usePortals } from 'react-portal-hook';
import { Carousel } from 'react-responsive-carousel';
import { SvgIcon, Button } from '@ui-elements';
import { money } from 'common/utils/formatters';
import { VehicleModel } from 'common/models';
import { useHashRouter } from '~/common/hooks/use-hashrouter';
import { ModelDetailPricing, PostcodeSelect } from 'common/components';
import { useVehicleModels } from '../finance-provider';
import styles from './index.module.scss';
import { Waypoint } from 'react-waypoint';

type CarouselOption = {
  percentage: number;
  centerMode: boolean;
  hasPrev?: boolean;
  hasNext?: boolean;
};

type Props = {
  onSectionVisible?: (url: string) => void;
};

export const FinanceVehicleCarousel = React.memo<Props>(
  ({ onSectionVisible }) => {
    const router = useHashRouter();
    const { vehicleModels } = useVehicleModels();

    const carouselRef = React.useRef<Carousel>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [currentSlide, setCurrentSlide] = React.useState<number>(1);

    const [options, setOptions] = React.useState<CarouselOption>({
      percentage: 33.3,
      centerMode: true,
      hasPrev: true,
      hasNext: true,
    });

    const [activeVehicle, setActiveVehicle] = React.useState<VehicleModel>({
      variants: vehicleModels as VehicleModel[],
    });

    React.useLayoutEffect(() => {
      if (isMobile()) {
        setCurrentSlide(0);
        setOptions({
          centerMode: false,
          percentage: 100,
        });
      }
    }, []);

    React.useEffect(() => {
      setOptions((opt) => ({
        ...opt,
        hasPrev: currentSlide > (isMobile() ? 0 : 1),
        hasNext:
          currentSlide <
          (activeVehicle?.variants?.length as number) - (isMobile() ? 1 : 2),
      }));
    }, [currentSlide, activeVehicle]);

    const isMobile = () => {
      if (containerRef.current === null) return false;
      return containerRef.current?.clientWidth < 923;
    };

    const adjustCarouselOptions = (vehicle: VehicleModel) => {
      if (isMobile()) return;
      if (!vehicle.variants) return;

      setOptions({
        centerMode: true,
        percentage:
          vehicle.variants.length === 1
            ? 100
            : vehicle.variants.length === 2
            ? 50
            : 33.3,
      });
    };

    const handleVehicleClick = (vehicle: VehicleModel) => {
      if (vehicle.variants && vehicle.variants?.length > 0) {
        setActiveVehicle(vehicle);
        adjustCarouselOptions(vehicle);

        const timeout = setTimeout(() => {
          const position = isMobile() ? 0 : 1;
          setCurrentSlide(position);
          carouselRef.current?.moveTo(position);
          clearTimeout(timeout);
        }, 100);
      } else {
        router.navigate('/financial-services/calculator', {
          params: {
            id: vehicle.vehicleId,
          },
        });
      }
    };

    const handleSectionVisible = (url: string) => {
      if (onSectionVisible) onSectionVisible(url);
    };

    return (
      <Waypoint
        topOffset={200}
        bottomOffset={500}
        onEnter={() => handleSectionVisible('/financial-services/calculators')}
      >
        <div id="vehicle-carousel" className={styles.Carousel}>
          <div ref={containerRef} className="container mx-auto relative">
            <p className={styles.Carousel_title}>Finance Calculator</p>

            <p className={styles.Carousel_subTitle}>
              {!activeVehicle.vehicleName
                ? 'Choose a model to start'
                : `Select your ${activeVehicle.vehicleName} variant`}
            </p>

            <PostcodeSelect>
              {({ name, postcode }, showPostcodeSelect) => (
                <p className={styles.PostcodeSelect}>
                  {`*Recommended Driveaway Price (RDP) shown based on ${name}, ${postcode}. RDP may change based on location. `}
                  <span onClick={showPostcodeSelect}>Enter your postcode</span>{' '}
                  for the RDP in your area.
                </p>
              )}
            </PostcodeSelect>

            {activeVehicle.vehicleName && (
              <Button
                type="bordered"
                className={styles.ChooseModel}
                onClick={() => {
                  handleVehicleClick({
                    variants: vehicleModels as VehicleModel[],
                  });
                }}
              >
                <i className={styles.ChooseModelBtn_icon} />
                <span>Select a different model</span>
              </Button>
            )}

            <Carousel
              swipeable
              ref={carouselRef}
              interval={1000 * 60}
              autoPlay={false}
              showThumbs={false}
              showStatus={false}
              infiniteLoop={false}
              showIndicators={false}
              onChange={setCurrentSlide}
              selectedItem={currentSlide}
              centerMode={options.centerMode}
              centerSlidePercentage={options.percentage}
              renderArrowPrev={(onClick) =>
                options.hasPrev && (
                  <CarouselArrow type="left" onClick={onClick} />
                )
              }
              renderArrowNext={(onClick) =>
                options.hasNext && (
                  <CarouselArrow type="right" onClick={onClick} />
                )
              }
            >
              {activeVehicle.variants?.map((model) => (
                <VehicleCarouselItem
                  model={model}
                  key={model.vehicleId}
                  onClick={handleVehicleClick}
                />
              ))}
            </Carousel>
          </div>
        </div>
      </Waypoint>
    );
  }
);

interface VehicleCarouselItem {
  model: VehicleModel;
  onClick: (model: VehicleModel) => void;
}

const VehicleCarouselItem = React.memo<VehicleCarouselItem>(
  ({ model, onClick }) => {
    const portalManager = usePortals();
    const { getVariant } = useVehicleModels();

    const variant = getVariant(model.vehicleId as string);

    const handleShowPrice = (event: React.MouseEvent<HTMLLabelElement>) => {
      event.stopPropagation();

      portalManager.open((portal) => (
        <ModelDetailPricing
          modelName={model.vehicleName as string}
          mlp={variant?.variantMLP as number}
          rdp={variant?.variantRDP as number}
          closeModal={portal.close}
          pimIdentifier=""
        />
      ));
    };

    return (
      <div
        id={model.vehicleId}
        className={styles.Vehicle_item}
        onClick={() => onClick(model)}
      >
        <img
          src={model.vehicleImage}
          className={styles.Vehicle_img}
          loading="lazy"
        />
        {model.vehicleBadge ? (
          <img
            className={styles.Vehicle_badge}
            src={model.vehicleBadge}
            loading="lazy"
          />
        ) : (
          <label className={styles.Vehicle_name}>{model.vehicleNameAlt}</label>
        )}
        <label className={styles.Vehicle_rdp} onClick={handleShowPrice}>
          <span>From {money(variant?.variantRDP ?? 0, 0)} RDP</span>
          <span>*</span>
        </label>
      </div>
    );
  }
);

const CarouselArrow = React.memo<{
  type: 'left' | 'right';
  onClick: () => void;
}>(({ type, onClick }) => (
  <div onClick={onClick} className={styles[`CarouselArrow_${type}`]}>
    {type === 'right' ? (
      <SvgIcon size={2} type="chevronRight" />
    ) : (
      <SvgIcon size={2} type="chevronLeft" />
    )}
  </div>
));
